import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import { dramas, genreOptions } from "@/mocks/dramas";
import { filterDramas, type FilterState } from "@/utils/drama";

const PAGE_SIZE = 12;
const suggestions = ["悬疑", "科幻", "家庭", "年代", "张艺谋", "张译"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [inputValue, setInputValue] = useState(query);
  const [filters, setFilters] = useState<FilterState>({
    categories: [],
    genres: [],
    year: "all",
    sort: "latest",
  });
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    setInputValue(query);
    setPage(1);
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `“${query}”搜索结果 - 国产精品视频久久`
      : "影视搜索 - 国产精品视频久久";
  }, [query]);

  const result = useMemo(() => filterDramas(dramas, filters, query), [filters, query]);
  const visible = result.slice(0, page * PAGE_SIZE);
  const hasMore = visible.length < result.length;

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = inputValue.trim();
    if (!keyword) {
      navigate("/search");
      return;
    }
    setSearchParams({ q: keyword });
    setPage(1);
  };

  const toggleGenre = (genre: string) => {
    setFilters((prev) => ({
      ...prev,
      genres: prev.genres.includes(genre)
        ? prev.genres.filter((g) => g !== genre)
        : [...prev.genres, genre],
    }));
    setPage(1);
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1 pt-16">
        <div className="w-full bg-background-100 px-4 py-8 md:px-8 md:py-10">
          <div className="mx-auto w-full max-w-6xl">
            <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-600">
              <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400" />
              <span className="text-foreground-900">搜索结果</span>
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              全站影视搜索
            </h1>
            <p className="mt-2 text-sm text-foreground-700">
              输入片名、导演、演员或类型关键词，快速找到对应的国产影视作品。
            </p>

            <form
              onSubmit={submit}
              className="mt-5 flex w-full flex-col gap-2 rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 sm:flex-row sm:items-center"
            >
              <div className="flex flex-1 items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center text-foreground-500">
                  <i className="ri-search-line text-base" />
                </span>
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="例如：长安十二时辰、王家卫、悬疑"
                  className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
                />
              </div>
              <button
                type="submit"
                className="shrink-0 rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-600">试试：</span>
              {suggestions.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => {
                    setInputValue(word);
                    setSearchParams({ q: word });
                  }}
                  className="rounded-md bg-background-200 px-2.5 py-1 text-xs text-foreground-800 transition-colors hover:bg-secondary-200 cursor-pointer whitespace-nowrap"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 py-8 md:px-8 md:py-10">
          <div className="mx-auto w-full max-w-6xl">
            <div className="flex flex-col gap-3 border-b border-background-200 pb-4 md:flex-row md:items-center md:justify-between">
              <p className="text-sm text-foreground-700">
                {query ? (
                  <>
                    关键词 <strong className="text-primary-700">「{query}」</strong> 找到{" "}
                    <strong className="text-foreground-950">{result.length}</strong> 条相关结果
                  </>
                ) : (
                  <>未输入关键词，当前展示全部作品，共 {result.length} 条</>
                )}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-full border border-background-300 bg-background-100 px-1 py-1">
                  {[
                    { value: "all", label: "全部" },
                    { value: "movie", label: "电影" },
                    { value: "tv", label: "电视剧" },
                  ].map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          categories: item.value === "all" ? [] : [item.value],
                        }))
                      }
                      className={`rounded-full px-3 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        (item.value === "all" && filters.categories.length === 0) ||
                        filters.categories.includes(item.value)
                          ? "bg-primary-600 text-background-50"
                          : "text-foreground-700 hover:text-primary-700"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters((prev) => ({ ...prev, year: e.target.value }))}
                  className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none cursor-pointer"
                >
                  <option value="all">全部年份</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                  <option value="2022">2022</option>
                  <option value="2010s">2010-2019</option>
                  <option value="2000s">2000-2009</option>
                </select>
                <select
                  value={filters.sort}
                  onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value }))}
                  className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs text-foreground-800 outline-none cursor-pointer"
                >
                  <option value="latest">最近更新</option>
                  <option value="hot">最受欢迎</option>
                  <option value="rating">评分最高</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-600">类型：</span>
              {genreOptions.slice(0, 10).map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => toggleGenre(genre)}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    filters.genres.includes(genre)
                      ? "border-primary-600 bg-primary-600 text-background-50"
                      : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>

            <div className="mt-6">
              {result.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                    <i className="ri-emotion-sad-line text-2xl" />
                  </span>
                  <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    未找到相关影视作品
                  </h2>
                  <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-600">
                    建议换个关键词试试，或减少筛选条件。也可以回到首页看推荐榜单。
                  </p>
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap cursor-pointer"
                    >
                      回到首页推荐
                    </Link>
                    <Link
                      to="/category"
                      className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 whitespace-nowrap cursor-pointer"
                    >
                      浏览全部分类
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
                    {visible.map((item) => (
                      <DramaCard
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        poster={item.poster}
                        year={item.year}
                        category={item.category}
                        genres={item.genres}
                        rating={item.rating}
                        tags={item.tags}
                        badge={item.category === "tv" ? "剧集" : undefined}
                      />
                    ))}
                  </div>
                  <div className="mt-8 flex flex-col items-center gap-3">
                    <p className="text-xs text-foreground-600">
                      已显示 {visible.length} / {result.length} 条
                    </p>
                    {hasMore ? (
                      <button
                        type="button"
                        onClick={() => setPage((p) => p + 1)}
                        className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                      >
                        加载更多结果
                        <i className="ri-arrow-down-line" />
                      </button>
                    ) : (
                      <span className="text-xs text-foreground-500">已经到底啦</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}